import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import Tree from "react-d3-tree";
import { DogIcon as Dog, PawPrintIcon as PawPrint, PlusIcon as Plus, GitForkIcon as GitFork, TreeStructureIcon as TreeStructure, ListBulletsIcon as ListBullets, MagnifyingGlassPlusIcon as MagnifyingGlassPlus, MagnifyingGlassMinusIcon as MagnifyingGlassMinus, CornersOutIcon as CornersOut } from "@phosphor-icons/react";
import { thumbUrl } from "@/lib/utils-app";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import AddPedigreeAncestorDialog from "@/components/AddPedigreeAncestorDialog";
import { useAuth } from "@/contexts/AuthContext";

/** Convert the backend pedigree tree into the shape react-d3-tree wants.
 *
 *  We now also emit a PLACEHOLDER child for every empty sire / dam slot
 *  when the node is editable by the caller — so the tree renders a
 *  dashed "+ Add ancestor" card where the missing ancestor would be.
 */
function treeToD3(node, depth, maxDepth, isAdmin) {
  if (!node) return null;
  const children = [];
  const canEditThisNode = isAdmin || node.owned_by_caller === true;
  const nextDepth = depth + 1;
  const canExpand = nextDepth < maxDepth;

  const mapSlot = (childNode, relation) => {
    if (childNode) {
      const child = treeToD3(childNode, nextDepth, maxDepth, isAdmin);
      if (child) return { ...child, _relation: relation };
      return null;
    }
    // Empty slot → emit a placeholder card if (a) the tree hasn't bottomed
    // out yet and (b) the user can attach ancestors to this parent.
    if (canExpand && canEditThisNode) {
      return {
        name: "__placeholder__",
        attributes: {},
        _placeholder: true,
        _parentId: node.id,
        _parentName: node.name || "",
        _relation: relation,
        children: [],
      };
    }
    return null;
  };

  const sireChild = mapSlot(node.sire, "sire");
  if (sireChild) children.push(sireChild);
  const damChild = mapSlot(node.dam, "dam");
  if (damChild) children.push(damChild);

  return {
    name: node.name || "Unknown",
    attributes: {
      breed: node.breed || "",
      sex: node.sex || "",
      regNum: node.registration_number || "",
    },
    _id: node.id,
    _photo: node.photo || "",
    _titles: Array.isArray(node.titles) ? node.titles : [],
    _sex: node.sex || "",
    _dob: node.dob || "",
    _pedigreeOnly: !!node.pedigree_only,
    _ownedByCaller: !!node.owned_by_caller,
    children,
  };
}

// Count how many empty ancestor slots are in the tree that the caller
// could fill — used for the top-of-page nudge banner.
function countFillableSlots(node, depth, maxDepth, isAdmin) {
  if (!node) return 0;
  const canEditThisNode = isAdmin || node.owned_by_caller === true;
  const next = depth + 1;
  if (next >= maxDepth) return 0;
  let c = 0;
  if (!node.sire && canEditThisNode) c += 1;
  if (!node.dam && canEditThisNode) c += 1;
  if (node.sire) c += countFillableSlots(node.sire, next, maxDepth, isAdmin);
  if (node.dam) c += countFillableSlots(node.dam, next, maxDepth, isAdmin);
  return c;
}

const NodeCard = ({ nodeDatum, navigate, onAddAncestor, isAdmin }) => {
  // Placeholder card — dashed, gold, clickable → opens the add dialog.
  if (nodeDatum._placeholder) {
    const relation = nodeDatum._relation || "sire";
    const label = relation === "sire" ? "Add known sire" : "Add known dam";
    return (
      <g
        onClick={() => onAddAncestor(nodeDatum._parentId, nodeDatum._parentName, relation)}
        style={{ cursor: "pointer" }}
        data-testid={`pedigree-add-${relation}-${nodeDatum._parentId}`}
      >
        <foreignObject x={-110} y={-32} width={220} height={64}>
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            style={{
              background: "#FBF7EC",
              border: "2px dashed #C9A227",
              borderRadius: 12,
              padding: "10px 12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              color: "#8A6A00",
              fontFamily: "Manrope, sans-serif",
              fontSize: 13,
              fontWeight: 600,
              height: "100%",
              boxSizing: "border-box",
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 700 }}>+</span>
            {label}
          </div>
        </foreignObject>
      </g>
    );
  }

  const color = nodeDatum._sex === "male" ? "#0E0E0E" : "#C1272D";
  return (
    <g
      onClick={() => nodeDatum._id && navigate(`/pedigree/${nodeDatum._id}`)}
      style={{ cursor: "pointer" }}
    >
      <foreignObject x={-110} y={-40} width={220} height={80}>
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          style={{
            background: "white",
            border: `1px solid #E5E5E5`,
            borderLeft: `4px solid ${color}`,
            borderRadius: 12,
            padding: 10,
            boxShadow: "0 8px 30px rgba(90,107,86,0.06)",
            display: "flex",
            gap: 10,
            alignItems: "center",
            fontFamily: "Manrope, sans-serif",
            position: "relative",
          }}
        >
          {/* Admin shortcut: a tiny link to /dogs/{id} where admin can
              use Admin Tools (delete / merge duplicate). Hidden for
              non-admins. Stops propagation so it doesn't trigger the
              parent g.onClick that re-roots the tree. */}
          {isAdmin && nodeDatum._id && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/dogs/${nodeDatum._id}`);
              }}
              title="Admin: open dog page"
              data-testid={`pedigree-admin-open-${nodeDatum._id}`}
              style={{
                position: "absolute",
                top: 4,
                right: 4,
                width: 22,
                height: 22,
                padding: 0,
                background: "rgba(193,39,45,0.08)",
                border: "1px solid rgba(193,39,45,0.4)",
                color: "#C1272D",
                borderRadius: 6,
                fontSize: 10,
                fontWeight: 700,
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              ↗
            </button>
          )}
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 8,
              overflow: "hidden",
              background: "#FAFAFA",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {nodeDatum._photo ? (
              <img
                src={thumbUrl(nodeDatum._photo)}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <PawPrint size={18} weight="duotone" color="#6B6B6B" />
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: "Spectral, serif",
                fontSize: 15,
                fontWeight: 500,
                color: "#0E0E0E",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {nodeDatum.name}
              {nodeDatum._pedigreeOnly && (
                <span
                  style={{
                    marginLeft: 6,
                    fontSize: 9,
                    fontWeight: 700,
                    color: "#8A6A00",
                    background: "#FBF7EC",
                    border: "1px solid #E5D08A",
                    borderRadius: 999,
                    padding: "1px 5px",
                    verticalAlign: "middle",
                  }}
                >
                  PEDIGREE-ONLY
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 11,
                color: "#6B6B6B",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {nodeDatum.attributes?.breed}
              {nodeDatum.attributes?.regNum ? ` · ${nodeDatum.attributes.regNum}` : ""}
            </div>
            {nodeDatum._titles?.length > 0 && (
              <div style={{ marginTop: 2 }}>
                {nodeDatum._titles.slice(0, 3).map((t, i) => (
                  <span
                    key={i}
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: "#C1272D",
                      background: "rgba(166,138,100,0.1)",
                      border: "1px solid rgba(166,138,100,0.2)",
                      borderRadius: 999,
                      padding: "1px 6px",
                      marginRight: 3,
                    }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </foreignObject>
    </g>
  );
};

/* ────────────────────────────────────────────────────────────────
 *  Text pedigree — screen-reader-friendly alternative view
 * ────────────────────────────────────────────────────────────────
 * Renders the pedigree as a properly-nested <ul>/<li> hierarchy so
 * AT users can navigate ancestry by list level instead of fighting
 * the SVG canvas tree. Why a real <ul>? Screen readers announce
 * "list with N items, level X" natively for nested lists — exactly
 * the mental model of a pedigree.
 *
 * Implemented iteratively (not as a recursive component) because
 * CRA's older babel pipeline trips on certain self-referential
 * components — we build a flat React.createElement tree from the
 * data instead, which babel handles cleanly.
 */
function buildTextPedigreeTree(node, relation, depth, key) {
  if (!node) return null;
  // The visual tree emits synthetic `__placeholder__` nodes for empty
  // ancestor slots so sighted users can click "+ Add ancestor" cards.
  // In the text view those would read as literal "__placeholder__"
  // which is noise — skip them entirely.
  if (node._placeholder || node.name === "__placeholder__") return null;
  const titlesLine = (node._titles || []).join(", ");
  const sexLabel = node._sex === "male" ? "Male" : node._sex === "female" ? "Female" : null;
  const parts = [];
  if (sexLabel) parts.push(sexLabel);
  if (node.attributes && node.attributes.breed) parts.push(node.attributes.breed);
  if (node._dob) parts.push("DOB " + node._dob);
  if (node.attributes && node.attributes.regNum) parts.push("Reg " + node.attributes.regNum);
  const meta = parts.join(" \u00B7 ");

  let genLabel;
  if (depth === 0) {
    genLabel = "Subject";
  } else if (depth === 1) {
    genLabel = relation === "sire" ? "Sire" : "Dam";
  } else {
    const lineWord = relation === "sire" ? "Sire's" : "Dam's";
    genLabel = lineWord + " line \u00B7 gen " + depth;
  }

  const headerEls = [
    React.createElement(
      "p",
      { key: "h", className: "text-[10px] font-bold uppercase tracking-[0.25em] text-gold" },
      genLabel
    ),
  ];
  if (node._id) {
    headerEls.push(
      React.createElement(
        "a",
        {
          key: "n",
          href: "/pedigree/" + node._id,
          className: "font-serif text-base text-ink text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded hover:underline",
          "data-testid": "text-pedigree-link-" + node._id,
          "aria-label": "Open pedigree for " + (node.name || "ancestor"),
        },
        node.name || "Unknown"
      )
    );
  } else {
    headerEls.push(
      React.createElement(
        "span",
        { key: "n", className: "font-serif text-base text-[#6B6B6B] italic" },
        node.name || "Unknown ancestor"
      )
    );
  }
  if (meta) {
    headerEls.push(
      React.createElement("p", { key: "m", className: "text-xs text-[#4A4A4A]" }, meta)
    );
  }
  if (titlesLine) {
    headerEls.push(
      React.createElement(
        "p",
        { key: "t", className: "text-xs text-[#C1272D] font-semibold" },
        "Titles: " + titlesLine
      )
    );
  }
  if (node._pedigreeOnly) {
    headerEls.push(
      React.createElement(
        "p",
        { key: "po", className: "text-[11px] text-[#6B6B6B] italic" },
        "Pedigree-only ancestor (no full profile)"
      )
    );
  }

  const card = React.createElement(
    "div",
    {
      className:
        "flex flex-col gap-1 py-2 px-3 rounded-lg border border-[#EDE6CD] bg-white",
    },
    headerEls
  );

  const childNodes = [];
  if (Array.isArray(node.children)) {
    for (let i = 0; i < node.children.length; i++) {
      const c = node.children[i];
      const childRel = c._relation || (i === 0 ? "sire" : "dam");
      const childKey = (c._id || ("k" + depth + "-" + i)) + "";
      const built = buildTextPedigreeTree(c, childRel, depth + 1, childKey);
      if (built) childNodes.push(built);
    }
  }

  const children = [card];
  if (childNodes.length > 0) {
    children.push(
      React.createElement(
        "ul",
        {
          key: "u",
          className: "ml-4 sm:ml-6 border-l border-[#F0E9D0] pl-4 mt-2",
        },
        childNodes
      )
    );
  }

  return React.createElement("li", { key: key, className: "my-2" }, children);
}

function TextPedigree({ tree }) {
  if (!tree) return null;
  const root = buildTextPedigreeTree(tree, "self", 0, "root");
  return React.createElement(
    "div",
    {
      className:
        "bg-white border border-[#EAEAEA] rounded-xl p-4 sm:p-6 shadow-luxury-light",
      "data-testid": "pedigree-text-view",
    },
    [
      React.createElement(
        "h2",
        { key: "sr", className: "sr-only" },
        "Text pedigree \u2014 accessible list view"
      ),
      React.createElement(
        "p",
        { key: "intro", className: "text-xs text-[#6B6B6B] mb-3 max-w-2xl" },
        "This is a screen-reader-friendly view of the same pedigree shown visually. Each list level is one generation back; activate any ancestor name to open their own pedigree page."
      ),
      React.createElement("ul", { key: "list", className: "list-none" }, root),
    ]
  );
}




export default function Pedigree() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rawTree, setRawTree] = useState(null);
  const [generations, setGenerations] = useState("4");
  const [dogs, setDogs] = useState([]);
  const [addDialog, setAddDialog] = useState(null); // { dogId, dogName, relation } | null
  // Honour the user's choice across page navigations — the most
  // common screen-reader pattern is "I always want the text view".
  // localStorage persists, defaults to "tree" so sighted users see
  // what they're used to.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem("pedigree-view-mode");
      if (saved) return saved;
      // Phones get the text view by default — a 4-gen horizontal SVG
      // tree in a ~390px viewport is unusable without pinching.
      return window.innerWidth < 640 ? "text" : "tree";
    } catch (_e) { return "tree"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pedigree-view-mode", viewMode); } catch (_e) { /* ignore */ }
  }, [viewMode]);

  const isAdmin = user?.role === "admin";
  const maxDepth = Number(generations);

  const loadTree = () => {
    if (!id) {
      setRawTree(null);
      return;
    }
    api
      .get(`/dogs/${id}/pedigree`, { params: { generations } })
      .then((r) => setRawTree(r.data))
      .catch(() => setRawTree(null));
  };

  useEffect(() => {
    api.get("/dogs").then((r) => setDogs(r.data)).catch(() => setDogs([]));
  }, []);

  // Auto-land on the user's first owned dog so the backfill UI is
  // discoverable immediately — otherwise a fresh user sees an empty
  // "Pick a dog" page and has no idea this is where they can fill in
  // their pedigree.
  useEffect(() => {
    if (id || dogs.length === 0) return;
    const mine = isAdmin ? dogs[0] : dogs.find((d) => d.created_by === user?.id) || dogs[0];
    if (mine?.id) navigate(`/pedigree/${mine.id}`, { replace: true });
  }, [id, dogs, isAdmin, user?.id, navigate]);

  useEffect(() => {
    loadTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, generations]);

  const tree = useMemo(
    () => (rawTree ? treeToD3(rawTree, 0, maxDepth, isAdmin) : null),
    [rawTree, maxDepth, isAdmin]
  );
  const fillableCount = useMemo(
    () => (rawTree ? countFillableSlots(rawTree, 0, maxDepth, isAdmin) : 0),
    [rawTree, maxDepth, isAdmin]
  );

  // ---- Auto-fit: measure the container and scale the tree to fill it ----
  const treeBoxRef = useRef(null);
  const [boxSize, setBoxSize] = useState({ w: 0, h: 0 });
  const [zoomOverride, setZoomOverride] = useState(null); // null = auto-fit
  const [fitKey, setFitKey] = useState(0);

  useEffect(() => {
    const el = treeBoxRef.current;
    if (!el || viewMode !== "tree") return undefined;
    const update = () => setBoxSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [viewMode, tree]);

  const fit = useMemo(() => {
    if (!tree || !boxSize.w) return { zoom: 0.8, translate: { x: 160, y: 300 } };
    const measure = (node) => {
      if (!node.children || node.children.length === 0) return { depth: 0, leaves: 1 };
      let d = 0;
      let l = 0;
      node.children.forEach((c) => {
        const m = measure(c);
        d = Math.max(d, m.depth + 1);
        l += m.leaves;
      });
      return { depth: d, leaves: l };
    };
    const m = measure(tree);
    const treeW = m.depth * 260 + 280; // nodeSize.x per level + card width
    const treeH = Math.max(m.leaves * 120, 180); // nodeSize.y × sibling separation
    const zoom = Math.max(0.3, Math.min((boxSize.w - 32) / treeW, (boxSize.h - 32) / treeH, 1));
    return { zoom, translate: { x: 130 * zoom + 16, y: boxSize.h / 2 } };
  }, [tree, boxSize]);
  const zoomUsed = zoomOverride ?? fit.zoom;

  const openAddDialogForRoot = () => {
    if (!rawTree) return;
    // Prefer missing sire first, then dam.
    const relation = !rawTree.sire ? "sire" : !rawTree.dam ? "dam" : "sire";
    setAddDialog({ dogId: rawTree.id, dogName: rawTree.name || "this dog", relation });
  };

  const handleAddAncestor = (dogId, dogName, relation) => {
    setAddDialog({ dogId, dogName: dogName || "ancestor", relation });
  };

  return (
    <AppShell>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">Pedigree</p>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">Ancestry tree</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select
            value={id || ""}
            onValueChange={(v) => navigate(`/pedigree/${v}`)}
          >
            <SelectTrigger className="w-64 max-w-full" data-testid="pedigree-dog-select">
              <SelectValue placeholder="Select a dog" />
            </SelectTrigger>
            <SelectContent>
              {dogs.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name} ({d.breed})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={generations} onValueChange={setGenerations}>
            <SelectTrigger className="w-40" data-testid="pedigree-generations-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 generations</SelectItem>
              <SelectItem value="4">4 generations</SelectItem>
              <SelectItem value="5">5 generations</SelectItem>
              <SelectItem value="6">6 generations</SelectItem>
            </SelectContent>
          </Select>
          {/* Accessibility view toggle — tree (visual) vs. text (a11y).
              Persisted via localStorage so AT users always land in
              their preferred mode. Role-group + aria-pressed make
              each side announce as a 2-state toggle button. */}
          <div
            role="group"
            aria-label="Pedigree view mode"
            className="inline-flex items-center rounded-full border border-[#E5E5E5] bg-white p-0.5"
            data-testid="pedigree-view-toggle"
          >
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-pressed={viewMode === "tree"}
              onClick={() => setViewMode("tree")}
              className={`rounded-full h-8 px-3 text-xs font-semibold ${
                viewMode === "tree"
                  ? "bg-ink text-white hover:bg-ink hover:text-white"
                  : "text-[#6B6B6B] hover:bg-cream/60"
              }`}
              data-testid="pedigree-view-tree-btn"
            >
              <TreeStructure size={14} weight="bold" className="mr-1.5" />
              Tree
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-pressed={viewMode === "text"}
              onClick={() => setViewMode("text")}
              className={`rounded-full h-8 px-3 text-xs font-semibold ${
                viewMode === "text"
                  ? "bg-ink text-white hover:bg-ink hover:text-white"
                  : "text-[#6B6B6B] hover:bg-cream/60"
              }`}
              data-testid="pedigree-view-text-btn"
            >
              <ListBullets size={14} weight="bold" className="mr-1.5" />
              Text
            </Button>
          </div>
        </div>
      </div>

      {/* Banner: nudge the owner/admin to fill in missing ancestors */}
      {id && rawTree && fillableCount > 0 && (
        <div
          className="mb-4 bg-[#FBF7EC] border border-[#E5D08A] rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid="pedigree-backfill-banner"
        >
          <GitFork size={22} weight="duotone" className="text-gold flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-ink">
              {fillableCount} missing ancestor{fillableCount !== 1 ? "s" : ""} you can fill in
            </p>
            <p className="text-xs text-[#6B6B6B] mt-0.5">
              Click any dashed gold card in the tree below, or start with the root —
              your submission goes to admin review and, once approved, appears on the
              pedigree and all printable certificates.
            </p>
          </div>
          <Button
            onClick={openAddDialogForRoot}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white h-9 text-sm"
            data-testid="pedigree-backfill-start-btn"
          >
            <Plus size={14} className="mr-1" />
            Add ancestor
          </Button>
        </div>
      )}

      {!id ? (
        <div className="relative bg-cream/80 border border-[#E2DAC2] rounded-2xl p-16 text-center overflow-hidden" data-testid="pedigree-empty-state">
          <div className="absolute inset-x-8 top-0 h-px bg-gold-hairline" />
          <div className="absolute inset-x-8 bottom-0 h-px bg-gold-hairline" />
          <div className="mx-auto mb-5 h-16 w-16 rounded-full bg-white text-gold flex items-center justify-center ring-1 ring-gold/40 shadow-[0_4px_18px_rgba(201,162,39,0.18)]">
            <Dog size={32} weight="duotone" />
          </div>
          <h3 className="font-spectral font-light text-2xl md:text-3xl tracking-tight mb-2">
            {dogs.length === 0 ? "Register a dog to start your pedigree" : "Pick a dog"}
          </h3>
          <p className="text-sm text-[#3a3a3a] max-w-md mx-auto leading-relaxed">
            {dogs.length === 0
              ? "Register your first dog from the Dogs page, then return here to visualise their pedigree tree and fill in the ancestors you know."
              : "Choose one of your dogs above to see their ancestry tree. Missing sires, dams, or grandparents can be filled in directly from the tree and are submitted to admins for review."}
          </p>
          {dogs.length === 0 && (
            <>
              <div className="gold-hairline mt-6 mb-5 max-w-xs mx-auto" />
              <Button
                onClick={() => navigate("/dogs/new")}
                className="rounded-full bg-ink hover:bg-crimson text-white"
                data-testid="pedigree-empty-register-btn"
              >
                <Plus size={14} className="mr-1" />
                Register a dog
              </Button>
            </>
          )}
        </div>
      ) : !tree ? (
        <div className="text-[#6B6B6B]">Loading tree…</div>
      ) : viewMode === "text" ? (
        <TextPedigree tree={tree} />
      ) : (
        <div
          ref={treeBoxRef}
          className="bg-white border border-[#EAEAEA] rounded-xl overflow-hidden shadow-luxury-light relative h-[52vh] sm:h-[70vh]"
          data-testid="pedigree-tree"
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gold-hairline z-10 pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-px bg-gold-hairline z-10 pointer-events-none" />
          <Tree
            key={`${id}-${generations}-${fitKey}-${zoomUsed.toFixed(3)}-${boxSize.w}`}
            data={tree}
            orientation="horizontal"
            translate={fit.translate}
            pathFunc="step"
            collapsible={false}
            separation={{ siblings: 1.2, nonSiblings: 1.5 }}
            nodeSize={{ x: 260, y: 100 }}
            renderCustomNodeElement={(rd3tProps) => (
              <NodeCard
                nodeDatum={rd3tProps.nodeDatum}
                navigate={navigate}
                onAddAncestor={handleAddAncestor}
                isAdmin={user?.role === "admin"}
              />
            )}
            zoom={zoomUsed}
          />
          {/* Zoom controls — mobile pinch-zoom works too, but these make
              the tree recoverable when someone gets lost in whitespace. */}
          <div className="absolute bottom-3 right-3 z-20 flex flex-col gap-1.5">
            <button
              onClick={() => setZoomOverride(Math.min(zoomUsed * 1.3, 2))}
              aria-label="Zoom in"
              className="h-9 w-9 rounded-full bg-white border border-[#E5E5E5] shadow-sm flex items-center justify-center text-ink hover:bg-cream active:scale-95 transition-transform"
              data-testid="pedigree-zoom-in"
            >
              <MagnifyingGlassPlus size={16} weight="bold" />
            </button>
            <button
              onClick={() => setZoomOverride(Math.max(zoomUsed / 1.3, 0.15))}
              aria-label="Zoom out"
              className="h-9 w-9 rounded-full bg-white border border-[#E5E5E5] shadow-sm flex items-center justify-center text-ink hover:bg-cream active:scale-95 transition-transform"
              data-testid="pedigree-zoom-out"
            >
              <MagnifyingGlassMinus size={16} weight="bold" />
            </button>
            <button
              onClick={() => { setZoomOverride(null); setFitKey((k) => k + 1); }}
              aria-label="Fit tree to screen"
              className="h-9 w-9 rounded-full bg-ink text-white shadow-sm flex items-center justify-center hover:bg-crimson active:scale-95 transition-transform"
              data-testid="pedigree-zoom-fit"
            >
              <CornersOut size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {addDialog && (
        <AddPedigreeAncestorDialog
          open={!!addDialog}
          onClose={() => setAddDialog(null)}
          dogId={addDialog.dogId}
          dogName={addDialog.dogName}
          defaultRelation={addDialog.relation}
          onSubmitted={() => {
            setAddDialog(null);
            loadTree();
          }}
        />
      )}
    </AppShell>
  );
}
